import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import './ConvertToProductionModal.css';
import { localToday } from '../../utils/dateUtils';

const ConvertToProductionModal = ({ quote, lines, onClose, onSuccess }) => {
  const { t } = useTranslation('sales');
  const navigate = useNavigate();
  const [selectedLines, setSelectedLines] = useState(
    lines.map(line => ({
      ...line,
      selected: true,
      convertQuantity: line.quantity // Default to full quantity
    }))
  );
  const [productionOrderData, setProductionOrderData] = useState({
    po_number: `PO-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
    quotation_id: quote.quotation_id || quote.id,
    customer_id: quote.customer_id,
    customer_name: quote.customer_name,
    planned_due_date: localToday(14),
    planned_start_date: localToday(),
    priority: 'NORMAL',
    status: 'PLANNED',
    notes: t('convertToProductionModal.createdFromQuote', { number: quote.quote_number || quote.quotation_number })
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLineSelection = (lineId) => {
    setSelectedLines(prevLines =>
      prevLines.map(line =>
        line.line_id === lineId
          ? { ...line, selected: !line.selected }
          : line
      )
    );
  };

  const handleQuantityChange = (lineId, newQuantity) => {
    setSelectedLines(prevLines =>
      prevLines.map(line =>
        line.line_id === lineId
          ? { ...line, convertQuantity: newQuantity }
          : line
      )
    );
  };

  const handleSelectAll = () => {
    const allSelected = selectedLines.every(line => line.selected);
    setSelectedLines(prevLines =>
      prevLines.map(line => ({ ...line, selected: !allSelected }))
    );
  };

  const handleFieldChange = (field, value) => {
    setProductionOrderData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConvert = async () => {
    const linesToConvert = selectedLines.filter(
      line => line.selected && parseFloat(line.convertQuantity) > 0
    );

    if (linesToConvert.length === 0) {
      setError(t('convertQuoteToProduction.toasts.noLinesSelectedMessage'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create production order
      const productionOrder = {
        ...productionOrderData,
        quotation_id: quote.quotation_id || quote.id,
        total_amount: linesToConvert.reduce((sum, line) => {
          const quantity = parseFloat(line.convertQuantity || 0); // Use convertQuantity
          const unitPrice = parseFloat(line.unit_price || 0);
          const discountPct = parseFloat(line.discount_percentage || 0);
          const taxPct = parseFloat(line.tax_percentage || 0);

          const subtotal = quantity * unitPrice;
          const discount = subtotal * (discountPct / 100);
          const afterDiscount = subtotal - discount;
          const tax = afterDiscount * (taxPct / 100);

          return sum + afterDiscount + tax;
        }, 0)
      };

      // Create production order
      const orderResponse = await apiFetch(getApiUrl('/api/production-orders'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productionOrder)
      });

      if (!orderResponse.ok) {
        throw new Error(t('convertToProductionModal.errors.createFailed'));
      }

      const newOrder = await orderResponse.json();

      // Create production order items from selected quote lines
      for (const line of linesToConvert) {
        const orderItem = {
          production_order_id: newOrder.id,
          product_id: line.product_id,
          product_name: line.product_name,
          product_code: line.product_code,
          quantity: line.convertQuantity, // Use convertQuantity instead of full quantity
          unit_price: line.unit_price,
          notes: line.description
        };

        await apiFetch(getApiUrl('/api/production-order-items'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderItem)
        });
      }

      // Update quote status to indicate it has been converted
      await apiFetch(getApiUrl(`/api/quotes/${quote.quotation_id || quote.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'CONVERTED',
          notes: `${quote.notes || ''}\n${t('convertToProductionModal.convertedNote', { number: newOrder.po_number || newOrder.order_number })}`
        })
      });

      // Success - navigate to production orders list
      alert(t('convertToProductionModal.createdSuccess', { number: newOrder.po_number || newOrder.order_number }));
      
      // Call onSuccess if provided, otherwise navigate directly
      if (onSuccess) {
        onSuccess(newOrder);
      } else {
        // Navigate to production page - the production tab will be remembered
        navigate('/production');
      }
      
      onClose();
    } catch (err) {
      setError(err.message || t('convertToProductionModal.errors.convertFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content convert-modal">
        <div className="modal-header">
          <h2>{t('convertQuoteToProduction.title')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="conversion-info">
            <div className="info-row">
              <span className="info-label">{t('convertQuoteToProduction.quoteNumber')}:</span>
              <span className="info-value">{quote.quote_number}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('common:ui.customer')}:</span>
              <span className="info-value">{quote.customer_name || t('common:ui.notAvailable')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('quotations.view.fields.totalAmount')}:</span>
              <span className="info-value">${parseFloat(quote.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="production-order-form">
            <h3>{t('convertToProductionModal.productionOrderDetails')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('production:fields.poNumber')}</label>
                <input
                  type="text"
                  value={productionOrderData.po_number}
                  onChange={(e) => handleFieldChange('po_number', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('convertQuoteToProduction.planning.plannedStartDate')}</label>
                <input
                  type="date"
                  value={productionOrderData.planned_start_date}
                  onChange={(e) => handleFieldChange('planned_start_date', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('convertQuoteToProduction.planning.plannedDueDate')}</label>
                <input
                  type="date"
                  value={productionOrderData.planned_due_date}
                  onChange={(e) => handleFieldChange('planned_due_date', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('common:ui.priority')}</label>
                <select
                  value={productionOrderData.priority}
                  onChange={(e) => handleFieldChange('priority', e.target.value)}
                >
                  <option value="LOW">{t('salesOrders.view.priorityOptions.low')}</option>
                  <option value="NORMAL">{t('salesOrders.view.priorityOptions.normal')}</option>
                  <option value="HIGH">{t('salesOrders.view.priorityOptions.high')}</option>
                  <option value="URGENT">{t('salesOrders.view.priorityOptions.urgent')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('common:ui.status')}</label>
                <select
                  value={productionOrderData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                >
                  <option value="PLANNED">{t('production:status.planned')}</option>
                  <option value="RELEASED">{t('production:status.released')}</option>
                  <option value="IN_PROGRESS">{t('production:status.inProgress')}</option>
                  <option value="ON_HOLD">{t('production:status.onHold')}</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>{t('common:ui.notes')}</label>
                <textarea
                  value={productionOrderData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="lines-selection">
            <div className="lines-header">
              <h3>{t('convertQuoteToProduction.selectLinesToConvert')}</h3>
              <button className="btn-link" onClick={handleSelectAll}>
                {selectedLines.every(line => line.selected) ? t('convertQuoteToProduction.deselectAll') : t('common:ui.selectAll')}
              </button>
            </div>

            <div className="lines-table-wrapper">
              <table className="lines-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedLines.every(line => line.selected)}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>{t('common:ui.product')}</th>
                    <th>{t('common:ui.description')}</th>
                    <th style={{ width: '100px' }}>{t('convertQuoteToProduction.columns.originalQty')}</th>
                    <th style={{ width: '120px' }}>{t('convertQuoteToProduction.columns.convertQty')}</th>
                    <th style={{ width: '100px' }}>{t('convertQuoteToProduction.columns.remaining')}</th>
                    <th>{t('common:ui.unitPrice')}</th>
                    <th>{t('common:ui.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLines.map((line) => {
                    const originalQuantity = parseFloat(line.quantity || 0);
                    const convertQuantity = parseFloat(line.convertQuantity || 0);
                    const remainingQuantity = originalQuantity - convertQuantity;
                    const unitPrice = parseFloat(line.unit_price || 0);
                    const discountPct = parseFloat(line.discount_percentage || 0);
                    const taxPct = parseFloat(line.tax_percentage || 0);

                    const subtotal = convertQuantity * unitPrice;
                    const discount = subtotal * (discountPct / 100);
                    const afterDiscount = subtotal - discount;
                    const tax = afterDiscount * (taxPct / 100);
                    const total = afterDiscount + tax;

                    return (
                      <tr key={line.line_id} className={line.selected ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={line.selected}
                            onChange={() => handleLineSelection(line.line_id)}
                          />
                        </td>
                        <td>{line.product_code} - {line.product_name}</td>
                        <td>{line.description || '-'}</td>
                        <td>{originalQuantity.toLocaleString()}</td>
                        <td>
                          <input
                            type="number"
                            className="quantity-input"
                            value={line.convertQuantity || ''}
                            onChange={(e) => handleQuantityChange(line.line_id, e.target.value)}
                            min="0"
                            max={originalQuantity}
                            step="1"
                            disabled={!line.selected}
                          />
                        </td>
                        <td className={remainingQuantity < 0 ? 'text-error' : ''}>
                          {remainingQuantity.toLocaleString()}
                        </td>
                        <td>${unitPrice.toFixed(2)}</td>
                        <td>${total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button
            className="btn-primary"
            onClick={handleConvert}
            disabled={loading || selectedLines.filter(l => l.selected && parseFloat(l.convertQuantity) > 0).length === 0}
          >
            {loading ? t('convertQuoteToProduction.converting') : t('convertQuoteToProduction.title')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertToProductionModal;